import {CheckoutRequestHandler} from "../requestHandlers/CheckoutRequestHandler";

class CountriesFactory {
  private awaitingCountriesPromise: Promise<ICountriesLookup[]> | null = null;
  private awaitingStatesPromise: Promise<IStateLookup[]> | null = null;
  private allCountries: ICountriesLookup[] | null = null;
  private allStates: {[field: string]: IStateLookup[]} | null = null;

  /**
   * get all countries
   * @param shopId
   */
  public async getAllCountries(shopId: number): Promise<ICountriesLookup[]> {
    if (this.allCountries) {
      return this.allCountries;
    }
    if (this.awaitingCountriesPromise) {
      await this.awaitingCountriesPromise;

      return this.allCountries!;
    }
    this.awaitingCountriesPromise = CheckoutRequestHandler.countriesLookup(shopId);
    this.allCountries = await this.awaitingCountriesPromise;
    this.awaitingCountriesPromise = null;

    return this.allCountries;
  }

  /**
   * get certain country
   * @param countryCode
   */
  public getCountryByCode(countryCode: string): ICountriesLookup | undefined {
    if (!this.allCountries) {
      return undefined;
    }

    return this.allCountries.find((country) => country.countryCode === countryCode);
  }

  /**
   * get all states of a country
   * @param shopId
   * @param countryCode
   */
  public async getStates(shopId: number, countryCode: string): Promise<IStateLookup[]> {
    if (this.allStates && this.allStates[countryCode]) {
      return this.allStates[countryCode];
    }

    if (this.awaitingStatesPromise) {
      try {
        await this.awaitingStatesPromise;
      } catch (err) {
        if (err.originalError?.response?.status !== 400) {
          throw err;
        }
      }

      return this.awaitingStatesPromise!;
    }

    let newStateLookup: {[field: string]: IStateLookup[]};
    try {
      this.awaitingStatesPromise = CheckoutRequestHandler.stateLookup(shopId, countryCode);
      newStateLookup = {[countryCode]: await this.awaitingStatesPromise};
    } catch (err) {
      if (err.originalError?.response?.status !== 400) {
        throw err;
      }
      newStateLookup = {[countryCode]: []};
    }

    this.allStates = {...this.allStates, ...newStateLookup};
    this.awaitingStatesPromise = null;

    return this.allStates[countryCode];
  }

  /**
   * get certain state of a country
   * @param countryCode
   * @param stateCode
   */
  public getStateByCode(countryCode: string, stateCode: string): IStateLookup | undefined {
    if (!this.allStates || !this.allStates[countryCode]) {
      return undefined;
    }

    return this.allStates[countryCode].find((state) => state.stateCode === stateCode);
  }
}

const countriesFactory = new CountriesFactory();
export {countriesFactory as CountriesFactory};

export interface ICountriesLookup {
  countryCode: string;
  name: string;
  isDeliveryAllowed: boolean;
  isGermany: boolean;
  isEU: boolean;
  isInternational: boolean;
  isStateRequired: boolean;
}

export interface IStateLookup {
  stateCode: string;
  name: string;
}
